/**
 * The signed-in frame (BUILD-PLAN 3.4 inverted L): side nav from module
 * manifests, top bar (office selector, palette trigger, freshness,
 * notifications, report a problem, theme, user menu), the routed page, and the global hosts
 * (palette, shortcut sheet, toasts, the call pop and the capture pill:
 * GlobalHosts.tsx).
 */
import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { PRODUCT } from '@velora/contracts/product';
import {
  AppShell,
  BrandMark,
  LockedModulePanel,
  PaletteTrigger,
  ToastHost,
  toast,
  TopBar,
  type NavLinkRenderProps,
} from '@velora/ui';
import { Menu as MenuIcon } from 'lucide-react';
import { Suspense, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { isMarketplaceModuleKey } from '@velora/contracts/marketplace';
import {
  MarketplaceLock,
  MarketplaceRequestNotifier,
  ModuleGate,
} from '../../modules/marketplace/index.js';
import { AssistantWidget, openAssistant } from '../../modules/assistant/widget/index.js';
import { FeedbackButton, FeedbackHost, WhatsNewHost } from '../../modules/feedback/index.js';
import { HelpButton } from '../../modules/help/HelpButton.js';
import { HelpHost } from '../../modules/help/HelpHost.js';
import { useQueryClient } from '@tanstack/react-query';
import { getLockedInfo } from '../builtin/modules.js';
import { SHELL_BOOTSTRAP_KEY } from '../identity/IdentityBridge.js';
import { useLive } from '../live/LiveProvider.js';
import { PaletteHost, type PaletteMode } from '../commands/PaletteHost.js';
import { isDemoMode } from '../demo/mode.js';
import type { WebModule } from '../module.js';
import { useOfficeScope } from '../office/OfficeScope.js';
import {
  DEFAULT_OFFICE_WHY,
  defaultOfficeFor,
  readLastOffice,
  rememberLastOffice,
} from '../office/defaultOffice.js';
import { scopeForRoute } from '../office/scope.js';
import { usePrefs } from '../prefs/prefs.js';
import { useDocumentTitle } from '../privacy/privacy.js';
import { sectionHidden, useEntitlement, useSession } from '../session/session.js';
import { shortcutLabels } from '../shortcuts/keys.js';
import { isMacPlatform } from '../shortcuts/platform.js';
import { ShortcutListener, ShortcutSheetHost } from '../shortcuts/ShortcutHost.js';
import { useActiveRoute } from './activeRoute.js';
import { ShellBindings } from './bindings.js';
import { ClassicFrame } from './ClassicFrame.js';
import type { NavEntry } from './classicModel.js';
import { SkinBindings } from './SkinBindings.js';
import { SelectPatientHost } from '../select-patient/SelectPatientHost.js';
import { useCurrentPatient } from '../patient/current.js';
import { patientNavHref, splitHref } from '../patient/follow.js';
import { PatientBar, SelectedPatientSync, useLeavePatientPage } from './PatientBar.js';
import { PageSlotHost, ShellGlobalHost } from '../skins/pageSlots.js';
import { SkinOnboarding } from '../skins/SkinOnboarding.js';
import { useSkin, useTerms } from '../skins/SkinProvider.js';
import { FreshnessMonitor } from './FreshnessMonitor.js';
import {
  GlobalCallPop,
  GlobalChat,
  GlobalReceptionModes,
  GlobalMessagesButton,
  GlobalMedicalAlerts,
  GlobalWriteRx,
  GlobalSensorPill,
} from './GlobalHosts.js';
import { navRoleOf } from './sidenav/model.js';
import { useOptionalSession as useIdentitySession } from '../../modules/identity/index.js';
import {
  effectiveNavCollapsed,
  useNarrowShell,
  useTightShell,
  wantsRailWhenTight,
} from './narrow.js';
import { activeNavId, buildNavActions, buildNavModel, navBadges } from './nav.js';
import { canInScope } from '../session/access.js';
import { NoAccess, OfficeRequired } from './states.js';
import { VeloraSideNav } from './sidenav/VeloraSideNav.js';
import {
  DemoBadge,
  FreshnessControl,
  NotificationsMenu,
  OfficeControl,
  ThemeToggle,
  UserMenu,
} from './TopBarParts.js';
import { TimeclockHost } from './TimeclockButton.js';
import { CallPopsToggle } from '../../modules/phones/global/CallPopsToggle.js';
import { AppearanceSwitch, QuickNavBindings } from './AppearanceSwitch.js';
import { FamilyTabs } from './FamilyTabs.js';
import { gateFor } from './gated.js';
import { GatedState } from './GatedState.js';
import { useIntegrations } from './sidenav/useNavLayout.js';
import { StatusChip } from './StatusChip.js';
import { PwaHost } from '../pwa/PwaHost.js';
import { OpenInMainListener, PopoutBlockedHint, PopoutMenu } from '../windows/PopoutMenu.js';
import { BackToBar, HistoryBackButton } from './BackNav.js';
import { WindowSync } from '../windows/WindowSync.js';

function renderLink({ href, className, children, ...rest }: NavLinkRenderProps) {
  const target = splitHref(href);
  // A module opened for the selected patient is a plain navigation: no
  // hover preload, so nothing about the patient loads before the click.
  const forPatient = target.search !== undefined || href.startsWith('/patients/');
  return (
    <Link
      to={target.to}
      // The root route's middleware keeps `?office=`.
      {...(target.search ? { search: target.search } : {})}
      {...(forPatient ? { preload: false as const } : {})}
      // The shell picks the one active item (longest matching path). The
      // router's own prefix match would also light up parents, so
      // /settings/offices showed Settings and Offices as current.
      activeOptions={{ exact: true, includeSearch: false }}
      activeProps={{}}
      // `active` kept for tests and print styles that predate aria-current.
      className={rest['aria-current'] ? `${className} active` : className}
      aria-current={rest['aria-current']}
    >
      {children}
    </Link>
  );
}

/**
 * Re-reads the shell bootstrap (nav locks, route gates) when a module is
 * turned on or off anywhere, so a trial that ends or a module turned off in
 * another tab takes effect without a reload.
 */
function EntitlementRefresher() {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: SHELL_BOOTSTRAP_KEY });
  useLive('marketplace.entitlement', refresh);
  useLive('shell.entitlement', refresh);
  return null;
}

/** Gated pages an admin chose to open anyway, for this tab's session. */
const openedGates = new Set<string>();

/**
 * A one-office page opened at "All offices": switch to the default office
 * (defaultOffice.ts) instead of the "Pick an office" wall, and say so once.
 */
function AutoOffice({
  locationId,
  why,
  title,
}: {
  locationId: string;
  why: string;
  title: string;
}) {
  const office = useOfficeScope();
  useEffect(() => {
    office.setScope({ kind: 'location', locationId }, { replace: true });
    const name = office.offices.find((o) => o.id === locationId)?.name ?? 'one office';
    toast({
      title: `${title}: showing ${name}`,
      description: `${title} works on one office at a time, so it opened at ${why}. Switch with the office selector or the L key.`,
    });
    // Once per mount: the scope change unmounts this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);
  return null;
}

/** The routed page with its office, permission and add-on gates (also the pop-out's body). */
export function RouteBody() {
  const active = useActiveRoute();
  const office = useOfficeScope();
  const entitlement = useEntitlement(active?.entitlementKey ?? null);
  const session = useSession();
  const navigate = useNavigate();
  const patient = useCurrentPatient();
  const classic = useSkin().layout === 'classic';
  const integrations = useIntegrations();
  const [, setGateTick] = useState(0);
  const gate = active ? gateFor(`${active.moduleId}:${active.route.id}`, integrations) : null;
  const canSetUpHere = canInScope(session, 'settings.manage', office.locationIds);
  const title = active?.route.title ?? PRODUCT.name;
  useDocumentTitle(active ? `${title} - ${PRODUCT.name}` : PRODUCT.name);

  // Remember the office used for each one-office module (the next "All
  // offices" visit opens there).
  const moduleId = active?.moduleId;
  const oneOffice = active?.route.officeScope === 'location';
  const pickedId = office.scope.kind === 'location' ? office.scope.locationId : null;
  useEffect(() => {
    if (moduleId && oneOffice && pickedId) {
      rememberLastOffice(session.practice.id, session.user.id, moduleId, pickedId);
    }
  }, [moduleId, oneOffice, pickedId, session.practice.id, session.user.id]);

  if (active) {
    const check = scopeForRoute(office.scope, active.route.officeScope);
    if (!check.ok) {
      const perm = active.route.permission;
      const pick = defaultOfficeFor({
        offices: office.offices,
        patientLocationId: patient?.homeLocationId,
        lastLocationId: readLastOffice(session.practice.id, session.user.id, active.moduleId),
        homeLocationId: session.defaultLocationId,
        ...(perm ? { allowed: (id: string) => canInScope(session, perm, [id]) } : {}),
      });
      if (pick) {
        return (
          <AutoOffice
            locationId={pick.locationId}
            why={DEFAULT_OFFICE_WHY[pick.reason]}
            title={title}
          />
        );
      }
      return (
        <OfficeRequired
          title={title}
          offices={office.offices}
          onPick={(id) => office.setScope({ kind: 'location', locationId: id })}
        />
      );
    }
    // The route's permission, in any office in scope (the API answers 403).
    const perm = active.route.permission;
    if (perm && !canInScope(session, perm, office.locationIds)) {
      const elsewhere = canInScope(
        session,
        perm,
        office.offices.map((o) => o.id),
      );
      return <NoAccess title={title} otherOffice={elsewhere} />;
    }
    const key = active.entitlementKey;
    if (key && isMarketplaceModuleKey(key) && !entitlement.active) {
      // Not on in this scope: the Marketplace's own lock shows the reason
      // (trial ended, turned off), the trial or turn-on sheet for buyers and
      // a real request for staff (MK-06, MK-10, MK-13). Read-only (or mixed)
      // asks the Marketplace live and keeps the page under a "turn back on" bar.
      return entitlement.readable ? (
        <ModuleGate feature={key}>
          <Outlet />
        </ModuleGate>
      ) : (
        <div className="shell-page shell-page--center">
          <MarketplaceLock feature={key} />
        </div>
      );
    }
    if (!entitlement.readable) {
      // Other keys are internal grants with nothing to buy or request.
      const info = getLockedInfo(active.moduleId);
      return (
        <div className="shell-page shell-page--center">
          <LockedModulePanel
            moduleName={title}
            pitch={info?.pitch ?? `${title} is not turned on for your practice.`}
            {...(info ? { features: info.bullets } : {})}
            onLearnMore={() => void navigate({ to: '/marketplace' })}
          />
        </div>
      );
    }
  }
  // A page whose only output is a service that is not connected shows one
  // state instead of zeros (gated.ts); admins can still open it.
  if (gate && !openedGates.has(gate.routeKey)) {
    return (
      <GatedState
        gate={gate}
        canSetUp={canSetUpHere}
        onOpenAnyway={() => {
          openedGates.add(gate.routeKey);
          setGateTick((n) => n + 1);
        }}
      />
    );
  }
  return (
    <PageSlotHost
      path={active?.route.path}
      imagingSystem={session.skinDefaults?.imagingSystem ?? null}
    >
      {classic ? null : <FamilyTabs />}
      <Outlet />
    </PageSlotHost>
  );
}

export function ShellFrame({ modules }: { modules: readonly WebModule[] }) {
  const session = useSession();
  const office = useOfficeScope();
  const prefs = usePrefs();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const active = useActiveRoute();
  const badges = useSyncExternalStore(navBadges.subscribe, navBadges.get, navBadges.get);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteMode, setPaletteMode] = useState<PaletteMode>('all');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [officeOpen, setOfficeOpen] = useState(false);
  const phone = useNarrowShell();
  const tight = useTightShell();
  // TC role test fix 23: the planner starts with the rail at tablet width.
  const narrow = phone || (tight && wantsRailWhenTight(pathname));
  const [narrowOpen, setNarrowOpen] = useState(false);
  const navCollapsed = effectiveNavCollapsed(prefs.navCollapsed, narrow, narrowOpen);
  // Phone width: the nav is an off-canvas drawer (app.css); a page change closes it.
  useEffect(() => {
    if (phone) setNarrowOpen(false);
  }, [pathname, phone]);
  const setNavCollapsed = (c: boolean) => {
    if (narrow) setNarrowOpen(!c);
    else prefs.set({ navCollapsed: c });
  };
  const isMac = isMacPlatform();

  const skin = useSkin();
  const t = useTerms();
  const nav = useMemo(
    () => buildNavModel(modules, session, office.locationIds, badges, t),
    [modules, session, office.locationIds, badges, t],
  );
  const navEntries = useMemo<NavEntry[]>(
    () =>
      nav.groups.flatMap((g) =>
        g.items.map((i) => ({
          id: i.id,
          moduleId: i.id.split(':')[0] ?? i.id,
          label: i.label,
          icon: i.icon,
          href: i.href,
          ...(i.badge ? { badge: i.badge } : {}),
          ...(i.locked ? { locked: true } : {}),
        })),
      ),
    [nav],
  );
  const navActions = useMemo(() => buildNavActions(modules, t), [modules, t]);
  const activeId = activeNavId(nav.paths, pathname, active?.moduleId);

  // Per-patient modules open on the selected patient (patient/follow.ts),
  // when this person may open that patient route in the offices in scope.
  const selected = useCurrentPatient();
  const selectedId = selected?.id ?? null;
  const routePerms = useMemo(
    () =>
      new Map(
        modules.flatMap((m) => m.manifest.routes.map((r) => [r.path, r.permission] as const)),
      ),
    [modules],
  );
  const canOpen = useCallback(
    (path: string) => {
      if (!routePerms.has(path)) return false;
      const perm = routePerms.get(path);
      return !perm || canInScope(session, perm, office.locationIds);
    },
    [routePerms, session, office.locationIds],
  );
  const sideEntries = useMemo(() => {
    if (!selectedId) return nav.entries;
    return nav.entries.map((e) => {
      const href = patientNavHref(e.id, e.href, selectedId, canOpen);
      return href === e.href ? e : { ...e, href };
    });
  }, [nav.entries, selectedId, canOpen]);
  // Unselect on one patient's page opens that module's own list (matched
  // from the path, so it holds while the route's match is still settling).
  const ownerList = useMemo(() => {
    const owner = modules.find((m) =>
      m.manifest.routes.some((r) =>
        new RegExp(`^${r.path.replace(/\$[^/]+/g, '[^/]+')}$`).test(pathname),
      ),
    )?.manifest.id;
    return owner ? nav.entries.find((e) => e.id.startsWith(`${owner}:`))?.href : undefined;
  }, [modules, pathname, nav.entries]);
  const leavePatientPage = useLeavePatientPage(ownerList);
  const canSetUp = canInScope(session, 'settings.manage', office.locationIds);
  // The sidebar starts from the person's role (pins, hidden, collapsed).
  const identity = useIdentitySession();
  const roleKeys = identity?.roles.map((r) => r.key).join(',') ?? '';
  const navRole = useMemo(
    () =>
      navRoleOf(roleKeys ? roleKeys.split(',') : [], (p) =>
        canInScope(session, p, office.locationIds),
      ),
    [roleKeys, session, office.locationIds],
  );
  const allowAll = active ? active.route.officeScope !== 'location' : true;
  const assistantPath = modules
    .flatMap((m) => m.manifest.routes)
    .find((r) => r.path === '/assistant')?.path;
  const assistantEntitlement = useEntitlement('ai_assistant');
  // The floating assistant answers palette questions when this person has it.
  // Settings > Roles > Module access can hide Ask Velora from a role.
  const askHidden = sectionHidden(session.hiddenModules, 'assistant.widget');
  const widgetAsks =
    !askHidden &&
    assistantEntitlement.readable &&
    canInScope(session, 'patient.view', office.locationIds);

  const openPalette = (mode: PaletteMode) => {
    setPaletteMode(mode);
    setPaletteOpen(true);
  };

  const activeNav = navEntries.find((n) => n.id === activeId);
  const officeControl = (
    <OfficeControl allowAll={allowAll} open={officeOpen} onOpenChange={setOfficeOpen} />
  );
  // Phone width only: opens the nav drawer (the rail is hidden there, app.css).
  const drawerButton = phone ? (
    <button
      type="button"
      className="shell-iconbtn shell-navdrawer-btn"
      aria-label="Open navigation"
      aria-expanded={narrowOpen}
      onClick={() => setNarrowOpen(true)}
    >
      <MenuIcon size={20} aria-hidden />
    </button>
  ) : null;
  const frame =
    skin.layout === 'classic' && skin.chrome ? (
      <ClassicFrame
        chrome={skin.chrome}
        nav={navEntries}
        activeId={activeId}
        activeModuleId={active?.moduleId}
        title={activeNav?.label ?? active?.route.title ?? PRODUCT.name}
        onPalette={() => openPalette('all')}
        menuBarRight={
          <div className="shell-topbar-actions">
            <HistoryBackButton />
            {isDemoMode() ? <DemoBadge /> : null}
            {officeControl}
            <FreshnessControl />
            <GlobalSensorPill />
            <AppearanceSwitch />
            <ThemeToggle />
            <CallPopsToggle />
            <PopoutMenu canOpen={canOpen} />
            <HelpButton />
            <FeedbackButton />
            <GlobalMessagesButton />
            <NotificationsMenu />
            <UserMenu onShortcuts={() => setSheetOpen(true)} />
          </div>
        }
      >
        <SkinOnboarding />
        <BackToBar />
        <RouteBody />
      </ClassicFrame>
    ) : (
      <AppShell
        navCollapsed={navCollapsed}
        nav={
          <VeloraSideNav
            entries={sideEntries}
            activeId={activeId}
            collapsed={navCollapsed}
            onCollapsedChange={setNavCollapsed}
            renderLink={renderLink}
            canSetUp={canSetUp}
            role={navRole}
            onShortcuts={() => setSheetOpen(true)}
            header={
              <Link to="/" className="shell-brand" aria-label={`${PRODUCT.name} home`}>
                <BrandMark name={PRODUCT.shortName} size={26} wordmark={!navCollapsed} />
              </Link>
            }
          />
        }
        topBar={
          <TopBar
            office={
              <>
                {drawerButton}
                <HistoryBackButton />
                {officeControl}
              </>
            }
            title={
              <>
                {isDemoMode() ? <DemoBadge /> : null}
                <PatientBar
                  onSearch={() => openPalette('patients')}
                  onUnselected={leavePatientPage}
                />
              </>
            }
            search={
              <PaletteTrigger
                onOpen={() => openPalette('all')}
                shortcut={shortcutLabels('mod+k', isMac)}
                placeholder="Search patients or jump to..."
              />
            }
            freshness={<FreshnessControl />}
            sensors={<StatusChip />}
            notifications={
              <>
                <AppearanceSwitch />
                <ThemeToggle />
                <CallPopsToggle />
                <PopoutMenu canOpen={canOpen} />
                <HelpButton />
                <FeedbackButton />
                <GlobalMessagesButton />
                <NotificationsMenu />
              </>
            }
            userMenu={<UserMenu onShortcuts={() => setSheetOpen(true)} />}
          />
        }
      >
        <SkinOnboarding />
        <BackToBar />
        <RouteBody />
        {phone && narrowOpen ? (
          <div className="shell-navdrawer-scrim" aria-hidden onClick={() => setNarrowOpen(false)} />
        ) : null}
      </AppShell>
    );

  return (
    <>
      {frame}

      <ShellBindings
        modules={modules}
        onPalette={openPalette}
        onShortcutSheet={() => setSheetOpen((v) => !v)}
        onOfficeMenu={() => setOfficeOpen(true)}
      />
      {/* After ShellBindings: the mode's keymap wins on shared keys. */}
      <SkinBindings
        navActions={navActions}
        onPalette={() => openPalette('all')}
        onShortcutSheet={() => setSheetOpen((v) => !v)}
        onOfficeMenu={() => setOfficeOpen(true)}
      />
      <QuickNavBindings />
      <SelectPatientHost onPalette={() => openPalette('patients')} />
      <SelectedPatientSync />
      <Suspense fallback={null}>
        <ShellGlobalHost />
      </Suspense>
      {askHidden ? null : (
        <AssistantWidget hidden={pathname.startsWith('/assistant')} routeKey={pathname} />
      )}
      <ShortcutListener />
      <FreshnessMonitor />
      <GlobalCallPop />
      <GlobalMedicalAlerts />
      <GlobalWriteRx />
      <FeedbackHost />
      <HelpHost />
      <WhatsNewHost />
      {skin.layout === 'classic' ? <TimeclockHost /> : null}
      <GlobalChat />
      <GlobalReceptionModes />
      <MarketplaceRequestNotifier />
      <EntitlementRefresher />
      <PaletteHost
        open={paletteOpen}
        mode={paletteMode}
        onOpenChange={setPaletteOpen}
        {...(widgetAsks
          ? { onAsk: (q: string) => openAssistant(q) }
          : assistantPath
            ? {
                // The question rides in history state, never the URL: what
                // people type here can name a patient (BUILD-PLAN 2.8).
                onAsk: (q: string) =>
                  void navigate({
                    to: assistantPath,
                    search: (prev: Record<string, unknown>) => prev,
                    state: { askVelora: q },
                  } as never),
              }
            : {})}
      />
      <ShortcutSheetHost
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        {...(active ? { screenName: active.route.title } : {})}
      />
      <ToastHost />
      {/* Pop-out windows: patient sync, Show in main window, pop-ups blocked. */}
      <WindowSync popout={null} />
      <OpenInMainListener />
      <PopoutBlockedHint />
      {/* Install sheet (phones and tablets), "Install app" menu entry, update prompt. */}
      <PwaHost />
    </>
  );
}
